import { tankMeasurements } from './tank_monitoring';
import { sensor_value_type_mapping as sensorTypes } from '../Constants/constants';
import { analogOverwritePercent } from './analog_overwrite';
import { hasOutputMonitorDisplay } from './output_monitor';
import { supportsMultiIoOverrides } from './multi_io_settings';

export function isMultiIOOverwriteActive(select, type) {
  const mode = Number(select);
  return Number(type) === 3
    ? [1, 2, 3].includes(mode)
    : [1, 2].includes(Number(type)) && [1, 3].includes(mode);
}

// Info uses controller readback only. A configured Auto row or a browser draft
// must not make a disabled output appear active.
export function isMultiIOInfoVisible(port, controller) {
  // Monitoring belongs to the physical channel, including disabled/unassigned IO.
  if (hasOutputMonitorDisplay(port.monitor)) return true;
  const type = controller.zones ? port.type_id : port.type;
  return (
    type !== 0 &&
    port.func != null &&
    (Number(port.func) !== 19 ||
      (supportsMultiIoOverrides(controller) && isMultiIOOverwriteActive(port.overwr_select, type)))
  );
}

export function getMultiIOOverwriteLabel(select, value, type, t) {
  if (!isMultiIOOverwriteActive(select, type)) return null;
  const state =
    Number(select) === 2
      ? `${value != null && Number.isFinite(Number(value)) ? analogOverwritePercent(value) : '---'} %`
      : t(Number(select) === 1 ? 'ov_on' : 'ov_off');
  return `${t('overwrite_by_io_hl')}: ${state}`;
}

export function getAvailableSettingNumber(value) {
  if (value == null || value === -1 || value === 0xffff) return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function getVpdSetpoint(settings, phase) {
  const useNightSetpoint = Number(phase) === 1 || Number(phase) === 3;
  return settings?.climate?.[useNightSetpoint ? 'vpd_sp_night' : 'vpd_sp'];
}

export function getHeatingMatSetpoint(settings, phase) {
  const climate = settings?.climate;
  const phaseName = Number(phase) === 1 ? 'night' : 'day';
  const absolute =
    getAvailableSettingNumber(climate?.[`toggle_temp_heating_mat_${phaseName}`]) === 1;
  const heatingMatSetting = getAvailableSettingNumber(
    climate?.[`temp_sp_heating_mat_${phaseName}_${absolute ? 'abs' : 'rel'}`]
  );

  if (heatingMatSetting == null) return null;
  if (absolute) return heatingMatSetting / 10;

  const roomTemperatureSetting = getAvailableSettingNumber(climate?.[`temp_sp_${phaseName}`]);
  return roomTemperatureSetting == null ? null : (heatingMatSetting + roomTemperatureSetting) / 10;
}

// Shared by the individual readings dialog and stored measurements.
export const externalReadingFormats = {
  [sensorTypes.SENS_VAL_TEMP_AIR]: { label: 'temp', digits: 1, unit: '°C', mainField: 'temp' },
  [sensorTypes.SENS_VAL_RH]: { label: 'humidity', digits: 1, unit: '% RH', mainField: 'humidity' },
  [sensorTypes.SENS_VAL_CO2]: { label: 'co2', digits: 0, unit: 'ppm', mainField: 'co2' },
  [sensorTypes.SENS_VAL_LEAF_T]: { label: 'temp_leaf_current', digits: 1, unit: '°C', mainField: 'temp_leaf_current' },
  [sensorTypes.SENS_VAL_ROOT_VWC]: { label: 'vol_water_content', digits: 1, unit: '%', mainField: 'vol_water_content', mainCount: 4 },
  [sensorTypes.SENS_VAL_ROOT_EC_PW]: { label: 'ec_root_zone', digits: 2, unit: 'mS/cm', mainField: 'ec_root_zone', mainCount: 4 },
  [sensorTypes.SENS_VAL_ROOT_TEMP]: { label: 'temp_root_zone', digits: 1, unit: '°C', mainField: 'temp_root_zone', mainCount: 4 },
  ...Object.fromEntries(tankMeasurements.map(({ type, key, digits, unit }) =>
    [type, { label: key, digits, unit }]))
};
